import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import { RestApiProvider } from 'src/app/services/shared/api.service';

// Aplica un periodo de gracia a recibos que ya existen (recalcula su vencimiento). No modifica
// la configuración predeterminada de la clave: eso se hace desde "Ver Configuraciones".
@Component({
  selector: 'app-gracia-recibos-dialog',
  templateUrl: './gracia-recibos-dialog.component.html',
  styleUrls: ['./gracia-recibos-dialog.component.css']
})
export class GraciaRecibosDialogComponent implements OnInit {
  clave: any;
  ramos: any = [];
  subramos: any = [];
  comisiones: any = [];

  ramo: any = 0;
  subramo: any = 0;
  periodo_inicial: number = null;
  periodo_subsecuente: number = null;
  configuracionActual: any = null;

  // Códigos de estatus y tipo de recibo de saam-api (recibos/models.py). Conciliado (6) no se ofrece.
  estatusOpciones = [
    { id: 4, label: 'Pendientes' },
    { id: 1, label: 'Pagados' },
    { id: 5, label: 'Liquidados' },
    { id: 9, label: 'Pago parcial' },
    { id: 3, label: 'Prorrogados' },
  ];
  estatus: any = { 4: true, 1: false, 5: false, 9: false, 3: false };
  tiposOpciones = [
    { id: 1, label: 'Pólizas' },
    { id: 2, label: 'Endosos (A)' },
    { id: 3, label: 'Notas de crédito (D)' },
  ];
  tipos: any = { 1: true, 2: true, 3: false };

  alcance: 'todos' | 'rango' = 'todos';
  fecha_desde = '';
  fecha_hasta = '';

  preview: any = null;
  verDetalle = false;
  loadingPreview = false;
  saving = false;
  private previewSeq = 0;
  private previewTimer: any;

  constructor(
    public dialogRef: MatDialogRef<GraciaRecibosDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private restApi: RestApiProvider,
    private toastr: ToastrService,
  ) {
    this.clave = data.clave;
  }

  async ngOnInit() {
    try {
      this.ramos = await this.restApi.get_ramos(this.clave.aseguradora_code);
    } catch (error) {
      this.toastr.error('Ocurrió un error al cargar los ramos');
    }
    try {
      this.comisiones = await this.restApi.get_comisiones(this.clave.id) || [];
    } catch (error) {
      // Sin configuraciones solo se pierde la precarga de los días.
      this.comisiones = [];
    }
  }

  async changeRamo() {
    this.subramo = 0;
    this.subramos = [];
    this.configuracionActual = null;
    this.limpiarPreview();
    if (!this.ramo || this.ramo == 0) { return; }
    try {
      this.subramos = await this.restApi.get_subramos(this.clave.aseguradora_code, this.ramo);
    } catch (error) {
      this.toastr.error('Ocurrió un error al cargar los subramos');
    }
  }

  changeSubramo() {
    // Precarga con la configuración vigente más reciente de ese subramo, si existe.
    const configs = (this.comisiones || [])
      .filter(c => c.subramo == this.subramo)
      .sort((a, b) => (b.efective_date || '').localeCompare(a.efective_date || '') || b.id - a.id);
    this.configuracionActual = configs.length ? configs[0] : null;
    if (this.configuracionActual) {
      this.periodo_inicial = this.configuracionActual.periodo_inicial || 0;
      this.periodo_subsecuente = this.configuracionActual.periodo_subsecuente || 0;
    }
    this.solicitarPreview();
  }

  get estatusSeleccionados(): number[] {
    return this.estatusOpciones.filter(o => this.estatus[o.id]).map(o => o.id);
  }

  get tiposSeleccionados(): number[] {
    return this.tiposOpciones.filter(o => this.tipos[o.id]).map(o => o.id);
  }

  get errorFormulario(): string {
    if (!this.ramo || this.ramo == 0) { return 'Seleccione un ramo.'; }
    if (!this.subramo || this.subramo == 0) { return 'Seleccione un subramo.'; }
    if (!this.diasValidos(this.periodo_inicial) || !this.diasValidos(this.periodo_subsecuente)) {
      return 'Capture los días de gracia (números enteros, 0 o mayores).';
    }
    if (!this.estatusSeleccionados.length) { return 'Seleccione al menos un estatus.'; }
    if (!this.tiposSeleccionados.length) { return 'Seleccione al menos un tipo de recibo.'; }
    if (this.alcance === 'rango') {
      if (!this.fecha_desde || !this.fecha_hasta) { return 'Capture ambas fechas del rango.'; }
      if (this.fecha_desde > this.fecha_hasta) { return 'La fecha inicial es posterior a la final.'; }
    }
    return null;
  }

  private diasValidos(valor: any) {
    return valor !== null && valor !== '' && Number.isInteger(Number(valor)) && Number(valor) >= 0;
  }

  private payload(dryRun: boolean) {
    const payload: any = {
      clave_id: this.clave.id,
      subramo_id: Number(this.subramo),
      periodo_inicial: Number(this.periodo_inicial),
      periodo_subsecuente: Number(this.periodo_subsecuente),
      estatus: this.estatusSeleccionados,
      tipos: this.tiposSeleccionados,
      dry_run: dryRun,
    };
    // El rango se aplica sobre fecha_inicio (inicio de vigencia del recibo).
    if (this.alcance === 'rango') {
      payload.fecha_desde = this.fecha_desde;
      payload.fecha_hasta = this.fecha_hasta;
    }
    return payload;
  }

  private limpiarPreview() {
    this.previewSeq++;
    this.preview = null;
    this.loadingPreview = false;
  }

  // Se llama en cada cambio de filtros; espera un momento para no disparar una petición por tecla.
  solicitarPreview() {
    clearTimeout(this.previewTimer);
    this.limpiarPreview();
    if (this.errorFormulario) { return; }
    this.previewTimer = setTimeout(() => this.cargarPreview(), 400);
  }

  private async cargarPreview() {
    const seq = ++this.previewSeq;
    this.loadingPreview = true;
    try {
      const response: any = await this.restApi.gracia_recibos(this.payload(true));
      if (seq !== this.previewSeq) { return; }
      this.preview = response && typeof response.total === 'number' ? response : null;
      if (!this.preview) { this.toastr.error('No fue posible obtener la vista previa.'); }
    } catch (err) {
      if (seq !== this.previewSeq) { return; }
      this.toastr.error(this.mensajeError(err, 'No fue posible obtener la vista previa.'));
    } finally {
      if (seq === this.previewSeq) { this.loadingPreview = false; }
    }
  }

  formato(n: number) {
    return (n || 0).toLocaleString('es-MX');
  }

  nombreEstatus(id: number): string {
    const o = this.estatusOpciones.find(e => e.id === id);
    return o ? o.label.replace(/s$/, '').toLowerCase() : '';
  }

  nombreTipo(id: number): string {
    return ({ 1: 'póliza', 2: 'endoso A', 3: 'nota D' } as any)[id] || '';
  }

  get detallePreview(): string {
    if (!this.preview) { return ''; }
    const partes = this.estatusOpciones
      .filter(o => this.preview.por_estatus && this.preview.por_estatus[o.id])
      .map(o => this.formato(this.preview.por_estatus[o.id]) + ' ' + o.label.toLowerCase());
    return partes.join(' · ');
  }

  async actualizar() {
    if (this.saving || this.errorFormulario) { return; }

    // Conteo fresco justo antes de confirmar, para que el número mostrado sea el real.
    let conteo: any;
    try {
      conteo = await this.restApi.gracia_recibos(this.payload(true));
    } catch (err) {
      this.toastr.error(this.mensajeError(err, 'No fue posible calcular los recibos a actualizar.'));
      return;
    }
    if (!conteo || typeof conteo.total !== 'number') {
      this.toastr.error('No fue posible calcular los recibos a actualizar.');
      return;
    }
    this.preview = conteo;
    if (conteo.total === 0) {
      this.toastr.info('No hay recibos que coincidan con los filtros seleccionados.');
      return;
    }

    const confirmacion = await Swal.fire({
      title: '¿Actualizar ' + this.formato(conteo.total) + ' recibos?',
      html: 'Estás por actualizar el período de gracia de los recibos existentes correspondientes a esta clave y subramo.<br>' +
        'Se actualizarán únicamente los recibos con los estatus seleccionados.<br>' +
        'Los recibos conciliados no serán modificados.<br><br>' +
        'Esta acción puede afectar múltiples recibos. ¿Deseas continuar?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, actualizar recibos',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
      focusCancel: true,
    });
    if (!confirmacion.value) { return; }

    this.saving = true;
    this.dialogRef.disableClose = true;
    try {
      const response: any = await this.restApi.gracia_recibos(this.payload(false));
      // El éxito se decide por la forma de la respuesta, no solo por el status HTTP.
      if (!response || typeof response.actualizados !== 'number') {
        throw { error: response };
      }
      let detalle = 'Se actualizaron ' + this.formato(response.actualizados) + ' recibos.';
      if (response.omitidos_sin_fecha) {
        detalle += '<br><small>' + this.formato(response.omitidos_sin_fecha) + ' se omitieron por no tener fecha de inicio.</small>';
      }
      await Swal.fire({ title: 'Períodos de gracia actualizados correctamente.', html: detalle, icon: 'success' });
      this.solicitarPreview();
    } catch (err) {
      Swal.fire({
        title: 'No se pudieron actualizar los recibos',
        text: this.mensajeError(err, 'Ocurrió un error al actualizar los períodos de gracia.'),
        icon: err && err.status === 504 ? 'warning' : 'error',
      });
    } finally {
      this.saving = false;
      this.dialogRef.disableClose = false;
    }
  }

  private mensajeError(err: any, porDefecto: string): string {
    const body = err && err.error;
    if (body && typeof body === 'object' && body.Error) { return body.Error; }
    if (typeof body === 'string' && body.length < 300) { return body; }
    return porDefecto;
  }

  cancel() {
    if (this.saving) { return; }
    this.dialogRef.close();
  }
}
